import { ReplaySubject } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpHeaders, HttpResponse } from '@angular/common/http';

import { ServiceResponse } from "@common/data-models/service/service-response";
import { NomWebServicesJson } from "@common/services/nom-web-services-json";
import { ErrorTypes } from '@common/data-models/misc/errors';
import { Simple3Des } from '@common/utils/simple-3des';
import { DataStore } from "@common/data-models/data-store/data-store";
import { HelloResults } from "@common/data-models/service/hello-results";
import { AsyncServiceBase } from "@common/services/async-service-base";

import { ILogonService } from "@common/interfaces/i-logon-service";
import { NavigationService } from "@common/services/navigation-service";

import { PlatformConvertor } from "@common/services/platform-convertor"
import { UserStaffInfo } from "@common/data-models/service/user-staff-info";

declare var serverConfig: { [id: string]: any };
declare var ssoSettings: Array<any>;
declare var ssoConfigSettings: any;

@Injectable({ providedIn: 'root' })
export class LogonService implements ILogonService {
    constructor(
        public nomWebServicesJson: NomWebServicesJson,
        public platformConvertor: PlatformConvertor
    ) {
        this.nomWebServicesJson.logonService = this;
    }

    public username: string = "";
    public password: string = "";
    public rememberMe: boolean = false;

    public hasActiveSession = new ReplaySubject<void>(1);
    public logonPassed = false;

    protected dayOfYear(d: Date): number {
        var yn = d.getFullYear();
        var mn = d.getMonth();
        var dn = d.getDate();
        var d1 = new Date(yn, 0, 1, 12, 0, 0); // noon on Jan. 1
        var d2 = new Date(yn, mn, dn, 12, 0, 0); // noon on input date
        var ddiff = Math.round((d2.getTime() - d1.getTime()) / 864e5);
        return ddiff + 1;
    }

    public async getProductsCount(): Promise<ServiceResponse<any>> {
        let response: HttpResponse<any> = null;

        try {
            await this.nomWebServicesJson.getActiveServiceAddress();
            response = await this.nomWebServicesJson.get(
                "/api/Navigation",
                {
                    headers: new HttpHeaders({ "Content-Type": "application/json" }),
                    responseType: 'json',
                    observe: 'response'
                });

        } catch (e) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to load products count!",
                detailMessage: `Failed to load products count! \n\rStackTrace: NomWebServicesJson.getProducsCount(); \n\rError: ${e}`
            }, ErrorTypes.WARNING);
            return new ServiceResponse<any>(false, "Failed to load Products Count from web service");
        }

        if (response && response.body) {

        } else {
            return new ServiceResponse<any>(false, 1);
        }
    }

    public async getSSOSettings(): Promise<ServiceResponse<Array<any>>> {
        let res = new ServiceResponse<Array<any>>(ssoSettings ? true : false, ssoSettings);
        if (!res.status) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to load SSO settings!",
                detailMessage: `Failed to load SSO settings!`
            }, ErrorTypes.WARNING);
            return new ServiceResponse<Array<string>>(false, "Failed to load SSO list from web service");
        }
        return res;
    }

    public async getSSOConfigSettings(): Promise<ServiceResponse<any>> {
        let res = new ServiceResponse<Array<any>>(ssoConfigSettings ? true : false, ssoConfigSettings);
        if (!res.status) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to load SSO settings!",
                detailMessage: `Failed to load SSO settings!`
            }, ErrorTypes.WARNING);
            return new ServiceResponse<any>(false, "Failed to load SSO list from web service");
        }
        return res;
    }

    public async logoutSSO(provider: string, payload: string): Promise<ServiceResponse<void>> {

        let response: HttpResponse<any> = null;
        try {
            await this.nomWebServicesJson.getActiveServiceAddress();

            let Encryptor = new Simple3Des('@pM0B1L3Z1pD!sk2k7L3tMe!n6');
            let encryptData = '@pM0B1L3!5TheB35t|' + this.dayOfYear(new Date());
            let devKey = Encryptor.encryptData(encryptData);

            response = await this.nomWebServicesJson.post(
                "/api/LogoutSSO",
                {
                    domainName: serverConfig["defaultDomain"],
                    devKey: devKey,
                    iClientType: 14,
                    ssoToken: payload,
                    ssoProvider: provider
                }, {
                headers: new HttpHeaders({ "Content-Type": "application/json" }),
                responseType: 'json',
                observe: 'response'
            });
        } catch (e) {
            this.nomWebServicesJson.clearSessionData();
            return new ServiceResponse<void>(false, serverConfig["TranslatedLabel-LoginError"]);
        }
        return new ServiceResponse<void>(true);
    }

    public async logonSSO(provider: string, payload: string, deviceTokenId?: string): Promise<ServiceResponse<void>> {
        let response: HttpResponse<any> = null;
        try {
            await this.nomWebServicesJson.getActiveServiceAddress();

            let Encryptor = new Simple3Des('@pM0B1L3Z1pD!sk2k7L3tMe!n6');
            let encryptData = '@pM0B1L3!5TheB35t|' + this.dayOfYear(new Date());
            let devKey = Encryptor.encryptData(encryptData);
            let tokenToSend = deviceTokenId ? deviceTokenId : "";
            if (localStorage.getItem("deviceTokenId") && tokenToSend == "") tokenToSend = localStorage.getItem("deviceTokenId");

            response = await this.nomWebServicesJson.post(
                "/api/LogonSSO",
                {
                    domainName: serverConfig["defaultDomain"],
                    devKey: devKey,
                    iClientType: 14,
                    ssoToken: payload,
                    ssoProvider: provider,
                    deviceToken: tokenToSend,
                    invitationId: localStorage.getItem("inviteToken")
                }, {
                headers: new HttpHeaders({ "Content-Type": "application/json" }),
                responseType: 'json',
                observe: 'response'
            });
        } catch (e) {
            this.nomWebServicesJson.clearSessionData();
            return new ServiceResponse<void>(false, serverConfig["TranslatedLabel-LoginError"]);
        }

        this.nomWebServicesJson.NomTokenId = response.headers.get("X-ENPS-TOKEN");
        this.nomWebServicesJson.AppTokenId = response.headers.get("X-ENPS-APP-TOKEN");

        localStorage.removeItem("inviteToken");
        if (this.platformConvertor.useFor["logon"]) {
            this.nomWebServicesJson.HelloResults = <HelloResults>Object.assign(new HelloResults, response.body.Hello);
            this.nomWebServicesJson.HelloResults.UserStaffInfo = <UserStaffInfo>Object.assign(new HelloResults, response.body.UserInformation)
        } else {
            this.nomWebServicesJson.HelloResults = <HelloResults>Object.assign(new HelloResults, response.body);
            this.nomWebServicesJson.HelloResults.Navigation = response.body.PSTSuiteNavigation;
            this.nomWebServicesJson.HelloResults.CustomCardFields = response.body.PSTCustomCardFields;
        }
        this.nomWebServicesJson.UserId = this.nomWebServicesJson.HelloResults.UserID;

        this.nomWebServicesJson.saveSessionData();
        this.nomWebServicesJson.noSession = false;

        this.hasActiveSession.next();
        await this.nomWebServicesJson.connectRTU();
        this.logonPassed = true;
        this.nomWebServicesJson.helloResultsLoadedSource.next({ loaded: true, login: true });

        return new ServiceResponse<void>(true);
    }

    public async logon(userName: string, password: string, deviceTokenId?: string, domainName?: string): Promise<ServiceResponse<void>> {
        let legacyResponse: HttpResponse<any> = null;
        try {
            await this.nomWebServicesJson.getActiveServiceAddress();
            let userNameEscaped = userName.toUpperCase().replace(/(\s|\.|_)/g, serverConfig["UserIDSpaceReplacementCharacter"]);
            this.nomWebServicesJson.UserId = userNameEscaped;
            let Encryptor = new Simple3Des('@pM0B1L3Z1pD!sk2k7L3tMe!n6');
            let encryptData = '@pM0B1L3!5TheB35t|' + this.dayOfYear(new Date());
            let devKey = Encryptor.encryptData(encryptData);
            let tokenToSend = deviceTokenId ? deviceTokenId : "";
            if (localStorage.getItem("deviceTokenId") && tokenToSend == "") tokenToSend = localStorage.getItem("deviceTokenId");
            legacyResponse = await this.nomWebServicesJson.post(
                "/api/Logon",
                {
                    staffUserId: userNameEscaped,
                    domainUserId: userNameEscaped,
                    password: password,
                    domainName: domainName ? domainName : serverConfig["defaultDomain"],
                    devKey: devKey,
                    iClientType: 14,
                    deviceToken: tokenToSend
                }, {
                headers: new HttpHeaders({ "Content-Type": "application/json" }),
                responseType: 'json',
                observe: 'response'
            });

        } catch (e) {
            this.nomWebServicesJson.clearSessionData();
            this.nomWebServicesJson.destroySession();
            if (e.status && e.status == 417) {
                return new ServiceResponse<void>(false, serverConfig["TranslatedLabel-LoginErrorNoGroup"]);
            } else if ((e._body && e._body.includes("Expired license key")) || (e.error && e.error.includes("Expired license key"))) {
                return new ServiceResponse<void>(false, serverConfig["TranslatedLabel-LicenseExpired"]);
            } else {
                return new ServiceResponse<void>(false, serverConfig["TranslatedLabel-LoginError"]);
            }
        }
        this.nomWebServicesJson.NomTokenId = legacyResponse.headers.get("X-ENPS-TOKEN");
        this.nomWebServicesJson.AppTokenId = legacyResponse.headers.get("X-ENPS-APP-TOKEN");
        let resp: any = legacyResponse.body;
        this.nomWebServicesJson.HelloResults = <HelloResults>Object.assign(new HelloResults, resp);

        this.nomWebServicesJson.HelloResults.Navigation = resp.PSTSuiteNavigation;
        this.nomWebServicesJson.HelloResults.CustomCardFields = resp.PSTCustomCardFields;

        this.nomWebServicesJson.UserId = this.nomWebServicesJson.HelloResults.UserID;

        this.nomWebServicesJson.Navigation = this.nomWebServicesJson.HelloResults.Navigation;

        this.nomWebServicesJson.saveSessionData();
        this.nomWebServicesJson.noSession = false;

        this.hasActiveSession.next();
        await this.nomWebServicesJson.connectRTU();
        this.logonPassed = true;
        this.nomWebServicesJson.helloResultsLoadedSource.next({ loaded: true, login: true });

        return new ServiceResponse<void>(true);
    }

    public async logonWithToken(userToken: string, loginFromEnpsClient?: boolean): Promise<ServiceResponse<void>> {
        try {
            await this.nomWebServicesJson.getActiveServiceAddress();

            let Encryptor = new Simple3Des('@pM0B1L3Z1pD!sk2k7L3tMe!n6');
            let encryptData = '@pM0B1L3!5TheB35t|' + this.dayOfYear(new Date());
            let devKey = Encryptor.encryptData(encryptData);
            let clientType = 14;
            let response: HttpResponse<any> = await this.nomWebServicesJson.post(
                "/api/LogonWithToken",
                {
                    userToken: userToken,
                    reuseExistingSession: false,
                    devKey: devKey,
                    iClientType: clientType
                }, {
                headers: new HttpHeaders({ "Content-Type": "application/json" }),
                responseType: 'json',
                observe: 'response'
            });

            this.nomWebServicesJson.NomTokenId = response.headers.get("X-ENPS-TOKEN");
            this.nomWebServicesJson.AppTokenId = response.headers.get("X-ENPS-APP-TOKEN");
            this.nomWebServicesJson.HelloResults = <HelloResults>Object.assign(new HelloResults, response.body);
            this.nomWebServicesJson.HelloResults.Navigation = response.body.PSTSuiteNavigation;
            this.nomWebServicesJson.HelloResults.CustomCardFields = response.body.PSTCustomCardFields;
            this.nomWebServicesJson.UserId = this.nomWebServicesJson.HelloResults.UserID;

            this.hasActiveSession.next();
            await this.nomWebServicesJson.connectRTU();
            this.logonPassed = true;
            this.nomWebServicesJson.helloResultsLoadedSource.next({ loaded: true, login: true });

            this.nomWebServicesJson.saveSessionData();

            return new ServiceResponse<void>(true);
        } catch (e) {
            this.nomWebServicesJson.clearSessionData();
            this.nomWebServicesJson.destroySession();
            return new ServiceResponse<void>(false, serverConfig["TranslatedLabel-LoginError"]);
        }
    }

    public async logout(deviceId?: string): Promise<ServiceResponse<void>> {
        let deviceNomId: string = "";
        try {
            DataStore.rtuSubscriptions = {};
            DataStore.realtimeUpdatesProviders["enps"].forceDisconnect();
            let response: HttpResponse<any> = await this.nomWebServicesJson.get(
                "/api/Logout",
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    params: {
                        deviceToken: deviceNomId
                    },
                    observe: 'response'
                });
            AsyncServiceBase.reset();
            if (response.ok) {
                this.nomWebServicesJson.clearSessionData();
                this.nomWebServicesJson.destroySession();
                //this.initialized = false;
                this.nomWebServicesJson.noSession = true;
                return new ServiceResponse<void>(true);
            } else {
                this.nomWebServicesJson.showErrorModal({
                    message: "Logout error"
                }, ErrorTypes.WARNING);
                this.nomWebServicesJson.clearSessionData();
                this.nomWebServicesJson.destroySession();
                return new ServiceResponse<void>(false, "Logout error");
            }
        } catch (e) {
            this.nomWebServicesJson.showErrorModal({
                message: "Logout error",
                detailMessage: `Logout error. \n\rStackTrace: NomWebServicesJson.logout(); \n\rError: ${e}`
            }, ErrorTypes.WARNING);
            this.nomWebServicesJson.clearSessionData();
            this.nomWebServicesJson.destroySession();
            return new ServiceResponse<void>(false, "Logout error");
        }
    }

    public async ping(): Promise<ServiceResponse<void>> {
        let res = await this.nomWebServicesJson.ping();
        if (res.status) {
            this.hasActiveSession.next();
            await this.nomWebServicesJson.connectRTU();
        }
        return res;
    }
}
